import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, Edit, CheckCircle, X, 
  Stethoscope, Activity, Calendar, Pill, FileText, 
  IndianRupee, Loader2, Filter, SlidersHorizontal, 
  Clock, Save, Ban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { 
  getBillingServices, 
  createBillingService, 
  updateBillingService,
  deleteBillingService 
} from '@/lib/api';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { useFormValidation } from '@/hooks/useFormValidation';
import type { BillingService } from '@/types';

interface BillingServicesTabProps {
  clinicId: string;
  doctorId?: string;  // Optional - receptionists don't have doctor_id
  onNotification: (type: 'success' | 'error', message: string) => void;
  showServiceModal?: boolean;
  onCloseServiceModal?: () => void;
  selectedService?: BillingService | null;
  onServiceSaved?: () => void;
  onEditService?: (service: BillingService) => void;
}

interface ServiceFormData {
  name: string;
  price: number;
  category: 'consultation' | 'diagnostic' | 'procedure' | 'medicine' | 'custom';
  is_active: boolean;
}

type LoadingOperation = 'load' | 'create' | 'update' | 'delete' | null;

export default function BillingServicesTab({
  clinicId,
  doctorId,
  onNotification,
  showServiceModal = false,
  onCloseServiceModal,
  selectedService: externalSelectedService = null,
  onServiceSaved,
  onEditService
}: BillingServicesTabProps) {
  // State management
  const [services, setServices] = useState<BillingService[]>([]);
  const [loadingOperation, setLoadingOperation] = useState<LoadingOperation>('load');
  
  // Enhanced search states
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState('all');
  const [serviceStatusFilter, setServiceStatusFilter] = useState('all');
  
  // Advanced search states
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const { formatCurrency } = useBillingUtils();

  // Computed values
  const isLoading = loadingOperation !== null;

  // Enhanced search suggestions
  const generateSearchSuggestions = (term: string) => {
    if (!term.trim()) return [];
    
    const suggestions = new Set<string>();
    
    services.forEach(service => {
      // Service name matches
      if (service.name.toLowerCase().includes(term.toLowerCase())) {
        suggestions.add(service.name);
      }
      
      // Category matches
      if (service.category.toLowerCase().includes(term.toLowerCase())) {
        suggestions.add(service.category);
      }
    });
    
    return Array.from(suggestions).slice(0, 5);
  };

  // Enhanced filtering logic
  const filteredServices = useMemo(() => {
    return services.filter(service => {
      // Enhanced search filter
      const searchLower = serviceSearchTerm.toLowerCase();
      const matchesSearch = serviceSearchTerm === '' || 
        service.name.toLowerCase().includes(searchLower) ||
        service.category.toLowerCase().includes(searchLower) ||
        service.price.toString().includes(serviceSearchTerm);

      // Category filter
      const matchesCategory = serviceCategoryFilter === 'all' || 
        service.category === serviceCategoryFilter;

      // Status filter
      const matchesStatus = serviceStatusFilter === 'all' ||
        (serviceStatusFilter === 'active' && service.is_active) ||
        (serviceStatusFilter === 'inactive' && !service.is_active);

      // Price range filter
      const matchesPriceRange = (!priceRange.min || service.price >= parseFloat(priceRange.min)) &&
        (!priceRange.max || service.price <= parseFloat(priceRange.max));

      return matchesSearch && matchesCategory && matchesStatus && matchesPriceRange;
    });
  }, [services, serviceSearchTerm, serviceCategoryFilter, serviceStatusFilter, priceRange]);

  // Search handlers
  const handleSearchChange = (value: string) => {
    setServiceSearchTerm(value);
    
    if (value.trim()) {
      const suggestions = generateSearchSuggestions(value);
      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = () => {
    if (serviceSearchTerm.trim() && !recentSearches.includes(serviceSearchTerm)) {
      setRecentSearches(prev => [serviceSearchTerm, ...prev.slice(0, 4)]);
    }
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setServiceSearchTerm(suggestion);
    setShowSuggestions(false);
    if (!recentSearches.includes(suggestion)) {
      setRecentSearches(prev => [suggestion, ...prev.slice(0, 4)]);
    }
  };

  const clearAllFilters = () => {
    setServiceSearchTerm('');
    setServiceCategoryFilter('all');
    setServiceStatusFilter('all');
    setPriceRange({ min: '', max: '' });
    setShowSuggestions(false);
  };

  const hasActiveFilters = serviceSearchTerm || serviceCategoryFilter !== 'all' || 
    serviceStatusFilter !== 'all' || priceRange.min || priceRange.max;

  // Load services
  const loadServices = async () => {
    try {
      setLoadingOperation('load');
      const data = await getBillingServices(clinicId, doctorId);
      
      // The API already handles different response formats internally
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      setServices([]);
      onNotification?.('error', 'Failed to load services');
    } finally {
      setLoadingOperation(null);
    }
  };

  // Initial load - only clinicId is required (receptionists don't have doctorId)
  useEffect(() => {
    if (clinicId) {
      loadServices();
    }
  }, [clinicId, doctorId]);

  // Helper function for category icons
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'consultation':
        return {
          icon: <Stethoscope className="w-5 h-5 text-primary" />,
          bg: "bg-primary/10"
        };
      case 'diagnostic':
        return {
          icon: <Activity className="w-5 h-5 text-medical-diagnosis" />,
          bg: "bg-medical-diagnosis/10"
        };
      case 'procedure':
        return {
          icon: <Calendar className="w-5 h-5 text-success" />,
          bg: "bg-success/10"
        };
      case 'medicine':
        return {
          icon: <Pill className="w-5 h-5 text-warning" />,
          bg: "bg-warning/10"
        };
      default:
        return {
          icon: <FileText className="w-5 h-5 text-muted-foreground" />,
          bg: "bg-muted/10"
        };
    }
  };

  // Service management handlers
  const handleEditServiceClick = (service: BillingService) => {
    if (onEditService) {
      onEditService(service);
    }
  };

  const handleToggleServiceStatus = async (service: BillingService) => {
    try {
      setLoadingOperation('update');
      
      await updateBillingService(service.id, {
        name: service.name,
        price: service.price,
        category: service.category,
        is_active: !service.is_active,
        sort_order: service.sort_order || 0
      });

      onNotification('success', `Service ${service.is_active ? 'deactivated' : 'activated'} successfully`);
      loadServices(); // Refresh services list
    } catch (error: any) {
      onNotification('error', error.message || 'Failed to update service status');
    } finally {
      setLoadingOperation(null);
    }
  };

  const handleSaveService = async (serviceData: ServiceFormData) => {
    try {
      setLoadingOperation(externalSelectedService ? 'update' : 'create');
      
      if (externalSelectedService) {
        // Update existing service
        await updateBillingService(externalSelectedService.id, {
          name: serviceData.name,
          price: serviceData.price,
          category: serviceData.category,
          is_active: serviceData.is_active,
          sort_order: externalSelectedService.sort_order || 0
        });

        onNotification('success', 'Service updated successfully');
      } else {
        // Create new service
        await createBillingService({
          clinic_id: clinicId,
          doctor_id: doctorId || null,  // Optional - receptionists don't have doctor_id
          name: serviceData.name,
          price: serviceData.price,
          category: serviceData.category,
          is_active: serviceData.is_active,
          sort_order: 0
        });

        onNotification('success', 'Service created successfully');
      }

      if (onServiceSaved) {
        onServiceSaved();
      }
      loadServices(); // Refresh services list
    } catch (error: any) {
      console.error('Service save error:', error);
      onNotification('error', error.message || (externalSelectedService ? 'Failed to update service' : 'Failed to create service'));
    } finally {
      setLoadingOperation(null);
    }
  };

  const handleDeleteService = async (service: BillingService) => {
    if (!confirm(`Are you sure you want to delete "${service.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingOperation('delete');
      await deleteBillingService(service.id);
      onNotification('success', 'Service deleted successfully');
      loadServices(); // Refresh services list
    } catch (error: any) {
      onNotification('error', error.message || 'Failed to delete service');
    } finally {
      setLoadingOperation(null);
    }
  };

  const handleCloseModal = () => {
    if (onCloseServiceModal) {
      onCloseServiceModal();
    }
  };

  return (
    <div className="space-y-2">

      {/* Enhanced Services Filters - Compact */}
      <Card className="p-2">
        <CardContent className="p-2">
          <div className="space-y-3">
            {/* Main Search Row */}
            <div className="flex flex-col lg:flex-row gap-2">
              {/* Enhanced Search Input */}
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name, category, or price..."
                  value={serviceSearchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit();
                    }
                    if (e.key === 'Escape') {
                      setShowSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (serviceSearchTerm && searchSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  className="pl-10 pr-10 h-9 text-sm"
                />
                
                {/* Clear Search */}
                {serviceSearchTerm && (
                  <button
                    onClick={() => {
                      setServiceSearchTerm('');
                      setShowSuggestions(false);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}

                {/* Search Suggestions Dropdown */}
                {showSuggestions && (searchSuggestions.length > 0 || recentSearches.length > 0) && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50">
                    <div className="p-2 space-y-1">
                      {/* Search Suggestions */}
                      {searchSuggestions.length > 0 && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground px-2 py-1">Suggestions</div>
                          {searchSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm flex items-center gap-2"
                            >
                              <Search className="w-3 h-3 text-muted-foreground" />
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Recent Searches */}
                      {recentSearches.length > 0 && !serviceSearchTerm && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground px-2 py-1 border-t border-border mt-2 pt-2">Recent</div>
                          {recentSearches.map((search, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionClick(search)}
                              className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm flex items-center gap-2"
                            >
                              <Clock className="w-3 h-3 text-muted-foreground" />
                              {search}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Quick Filters */}
              <div className="flex gap-1.5">
                <Select value={serviceCategoryFilter} onValueChange={setServiceCategoryFilter}>
                  <SelectTrigger className="w-32 h-9 text-sm">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="diagnostic">Diagnostic</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                    <SelectItem value="medicine">Medicine</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={serviceStatusFilter} onValueChange={setServiceStatusFilter}>
                  <SelectTrigger className="w-24 h-9 text-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>

                {/* Advanced Search Toggle */}
                <Button
                  variant={showAdvancedSearch ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className="h-9 px-3"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-1" />
                  Filters
                </Button>
              </div>
            </div>

            {/* Advanced Search Panel */}
            {showAdvancedSearch && (
              <div className="border border-border rounded-lg p-3 bg-muted/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Price Range */}
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">Price Range</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <IndianRupee className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="number"
                          placeholder="Min"
                          value={priceRange.min}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                          className="pl-7 h-8 text-sm"
                        />
                      </div>
                      <span className="text-muted-foreground">to</span>
                      <div className="relative flex-1">
                        <IndianRupee className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={priceRange.max}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                          className="pl-7 h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quick Price Filters */}
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">Quick Price Filters</label>
                    <div className="flex flex-wrap gap-1">
                      {[
                        { label: 'Under ₹500', min: '', max: '500' },
                        { label: '₹500-1000', min: '500', max: '1000' },
                        { label: '₹1000-2000', min: '1000', max: '2000' },
                        { label: 'Above ₹2000', min: '2000', max: '' }
                      ].map((range) => (
                        <button
                          key={range.label}
                          onClick={() => setPriceRange({ min: range.min, max: range.max })}
                          className="px-2 py-1 text-xs border border-border rounded hover:bg-muted transition-colors"
                        >
                          {range.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Results Summary */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between p-2 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2 text-sm">
                  <Filter className="w-4 h-4 text-primary" />
                  <span className="text-primary font-medium">
                    Showing {filteredServices.length} of {services.length} services
                  </span>
                  
                  {/* Active Filter Tags */}
                  <div className="flex flex-wrap gap-1 ml-2">
                    {serviceSearchTerm && (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs border border-primary/20">
                        Search: "{serviceSearchTerm}"
                      </span>
                    )}
                    {serviceCategoryFilter !== 'all' && (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs border border-primary/20 capitalize">
                        {serviceCategoryFilter}
                      </span>
                    )}
                    {serviceStatusFilter !== 'all' && (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs border border-primary/20 capitalize">
                        {serviceStatusFilter}
                      </span>
                    )}
                    {(priceRange.min || priceRange.max) && (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs border border-primary/20">
                        {priceRange.min && priceRange.max 
                          ? `₹${priceRange.min}-₹${priceRange.max}`
                          : priceRange.min 
                            ? `Above ₹${priceRange.min}`
                            : `Under ₹${priceRange.max}`
                        }
                      </span>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="h-7 px-2 text-primary hover:text-primary-foreground hover:bg-primary"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              </div>
                         )}
           </div>
         </CardContent>
       </Card>

       {/* Compact Stats Strip */}
       <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/20 rounded-lg px-3 py-2">
         {/* Quick Stats - Compact Inline */}
         <div className="flex items-center gap-4">
           <div className="flex items-center gap-1">
             <Stethoscope className="w-3 h-3" />
             <span className="font-medium">{services.length}</span>
             <span>services</span>
           </div>
           
           <div className="flex items-center gap-1">
             <CheckCircle className="w-3 h-3 text-success" />
             <span className="font-medium text-success">{services.filter(s => s.is_active).length}</span>
             <span>active</span>
           </div>
           
           <div className="flex items-center gap-1">
             <IndianRupee className="w-3 h-3" />
             <span className="font-medium">
               {services.length > 0 ? formatCurrency(
                 services.reduce((sum, s) => sum + s.price, 0) / services.length
               ) : formatCurrency(0)}
             </span>
             <span>avg</span>
           </div>
         </div>
         
         {/* Category Pills - Compact */}
         <div className="flex items-center gap-1">
           {['consultation', 'diagnostic', 'procedure', 'medicine', 'custom'].map(category => {
             const categoryServices = services.filter(s => s.category === category);
             const categoryIcon = getCategoryIcon(category);
             
             if (categoryServices.length === 0) return null;
             
             return (
               <div key={category} className="flex items-center gap-0.5 bg-background rounded-full px-2 py-0.5 border border-border/50">
                 <div className={cn("w-3 h-3 rounded-sm flex items-center justify-center", categoryIcon.bg)}>
                   {React.cloneElement(categoryIcon.icon, { className: "w-2 h-2" })}
                 </div>
                 <span className="text-xs font-medium">{categoryServices.length}</span>
               </div>
             );
           })}
         </div>
       </div>

      {/* Compact Services Table */}
      <div className="space-y-2">
        {filteredServices.length === 0 ? (
          <Card className="medical-card">
            <CardContent className="text-center py-8">
              <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center mb-3">
                <Stethoscope className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1">No services found</h3>
              <p className="text-muted-foreground mb-3 text-xs">
                {serviceSearchTerm || serviceCategoryFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Use the Add Service button in the header to create your first billing service'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="medical-card">
            <CardContent className="p-0">
              <div className="overflow-hidden">
                {/* Table Header */}
                <div className="bg-muted/30 px-3 py-2 border-b grid grid-cols-12 items-center text-xs font-medium text-muted-foreground">
                  <div className="col-span-1"></div>
                  <div className="col-span-4">Service</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-2">Price</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                
                {/* Service Rows */}
                <div className="divide-y divide-border">
                  {filteredServices.map((service) => (
                    <div key={service.id} className="px-3 py-2 grid grid-cols-12 items-center hover:bg-muted/20 transition-colors">
                      {/* Icon */}
                      <div className="col-span-1">
                        <div className={cn(
                          "w-6 h-6 rounded-md flex items-center justify-center",
                          getCategoryIcon(service.category).bg
                        )}>
                          {React.cloneElement(getCategoryIcon(service.category).icon, { className: "w-3 h-3" })}
                        </div>
                      </div>

                      {/* Service Name */}
                      <div className="col-span-4">
                        <div className="font-medium text-sm text-foreground">{service.name}</div>
                      </div>

                      {/* Category */}
                      <div className="col-span-2">
                        <div className="text-xs text-muted-foreground capitalize">{service.category}</div>
                      </div>

                      {/* Price */}
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-foreground">{formatCurrency(service.price)}</div>
                      </div>

                      {/* Status */}
                      <div className="col-span-1">
                        {service.is_active ? (
                          <Badge className="bg-success/10 text-success border-success/20 text-xs h-5 px-1.5">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs h-5 px-1.5">
                            Inactive
                          </Badge>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="col-span-2 flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditServiceClick(service)}
                          className="h-6 w-6 p-0"
                          title="Edit service"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleToggleServiceStatus(service)}
                          className={cn(
                            "h-6 w-6 p-0",
                            service.is_active 
                              ? "hover:bg-warning/10 hover:text-warning" 
                              : "hover:bg-success/10 hover:text-success"
                          )}
                          title={service.is_active ? "Deactivate service" : "Activate service"}
                        >
                          {service.is_active ? (
                            <X className="w-3 h-3" />
                          ) : (
                            <CheckCircle className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Stethoscope className="w-4 h-4 text-primary" />
              {externalSelectedService ? 'Edit Service' : 'Create Service'}
            </DialogTitle>
          </DialogHeader>

          <ServiceForm
            service={externalSelectedService}
            onSubmit={handleSaveService}
            onCancel={handleCloseModal}
            loading={isLoading}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Service Form Component
function ServiceForm({ 
  service, 
  onSubmit, 
  onCancel, 
  loading 
}: {
  service?: BillingService | null;
  onSubmit: (data: ServiceFormData) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [formData, setFormData] = useState<ServiceFormData>({
    name: service?.name || '',
    price: service?.price || 0,
    category: (service?.category as ServiceFormData['category']) || 'consultation',
    is_active: service?.is_active !== undefined ? service.is_active : true,
  });

  const { validationErrors, validateFields, clearError, clearAllErrors } = useFormValidation<ServiceFormData>();

  // Reset form when service prop changes
  useEffect(() => {
    setFormData({
      name: service?.name || '',
      price: service?.price || 0,
      category: (service?.category as ServiceFormData['category']) || 'consultation',
      is_active: service?.is_active !== undefined ? service.is_active : true,
    });
    clearAllErrors();
  }, [service?.id, clearAllErrors]); // Use service.id instead of entire service object

  const validateForm = (): boolean => {
    return validateFields(formData, {
      name: (value) => !value?.trim() ? 'Service name is required' : undefined,
      price: (value) => {
        if (value === undefined || value === null || isNaN(value)) {
          return 'Price is required and must be a valid number';
        }
        if (value <= 0) {
          return 'Price must be greater than 0';
        }
        if (value > 999999) {
          return 'Price cannot exceed ₹9,99,999';
        }
        return undefined;
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Double-check validation before submitting
    if (!formData.name?.trim()) {
      return;
    }
    
    if (!formData.price || formData.price <= 0 || isNaN(formData.price)) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };
  
  // Check if form is valid for enabling submit button
  const isFormValid = formData.name?.trim() && formData.price > 0 && !isNaN(formData.price);

  return (
    <form 
      onSubmit={handleSubmit} 
      className="space-y-3"
      onKeyDown={(e) => {
        // Prevent form submission on Enter key in input fields
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
          e.preventDefault();
        }
      }}
    >
      <div className="space-y-3">
        {/* Service Name */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Service Name *
          </label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (validationErrors.name) {
                clearError('name');
              }
            }}
            placeholder="Enter service name"
            className={cn("h-8 text-sm", validationErrors.name ? 'border-destructive' : '')}
            required
          />
          {validationErrors.name && (
            <p className="text-xs text-destructive mt-1">{validationErrors.name}</p>
          )}
        </div>

        {/* Price */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Price *
          </label>
          <div className="relative">
            <IndianRupee className="w-3 h-3 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="number"
              min="0.01"
              max="999999"
              step="0.01"
              value={formData.price || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Handle empty string or allow valid numeric input
                if (value === '') {
                  setFormData({ ...formData, price: 0 });
                } else {
                  const numericValue = parseFloat(value);
                  if (!isNaN(numericValue) && numericValue >= 0) {
                    setFormData({ ...formData, price: numericValue });
                  }
                }
                if (validationErrors.price) {
                  clearError('price');
                }
              }}
              onKeyDown={(e) => {
                // Prevent minus key, 'e', 'E', and '+' for exponential notation
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                  e.preventDefault();
                }
              }}
              placeholder="0.00"
              className={cn("pl-8 h-8 text-sm", validationErrors.price ? 'border-destructive' : '')}
              required
            />
          </div>
          {validationErrors.price && (
            <p className="text-xs text-destructive mt-1">{validationErrors.price}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">
            Category
          </label>
          <Select 
            value={formData.category} 
            onValueChange={(value: ServiceFormData['category']) => 
              setFormData({ ...formData, category: value })
            }
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consultation">Consultation</SelectItem>
              <SelectItem value="diagnostic">Diagnostic</SelectItem>
              <SelectItem value="procedure">Procedure</SelectItem>
              <SelectItem value="medicine">Medicine</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Active Status */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="h-3 w-3 rounded border-border text-primary focus:ring-primary focus:ring-1 focus:ring-offset-1"
          />
          <label htmlFor="is_active" className="text-xs font-medium text-foreground">
            Active Service
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button 
          type="button"
          variant="outline" 
          size="sm"
          onClick={onCancel}
          disabled={loading}
          className="h-8 px-3"
        >
          <Ban className="w-3 h-3 mr-1" />
          Cancel
        </Button>
        <Button 
          type="submit"
          size="sm"
          disabled={loading || !isFormValid}
          className="bg-primary hover:bg-primary/80 h-8 px-3"
        >
          {loading ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              {service ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            <>
              <Save className="w-3 h-3 mr-1" />
              {service ? 'Update' : 'Create'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
} 